/**
 * Stripe Service
 * Handles all Stripe-related operations
 */

import { DirectusService } from './directusService';

interface CheckoutSession {
  sessionId: string;
  url: string;
}

interface CartItem {
  product_id: number | string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  stripe_price_id?: string;
}

export class StripeService {
  private static readonly API_URL = import.meta.env.VITE_DIRECTUS_URL;

  /** Directus flow UUIDs for Stripe operations */
  private static readonly FLOW_CHECKOUT = 'e38c9643-e227-4d04-9608-dc7750a99ce3';
  private static readonly FLOW_GET_SESSION = '5c795e89-d2bd-46e4-800f-3ed88f5d58ee';

  /**
   * Create a Stripe Checkout Session
   * Supports both stripe_price_id (preferred) and inline price_data
   */
  static async createCheckoutSession(
    items: CartItem[],
    userId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<CheckoutSession> {
    try {
      const token = await DirectusService.getToken();

      if (!token) {
        throw new Error('User must be authenticated to checkout');
      }

      // Build line items — use stripe_price_id when available, fallback to price_data
      const lineItems = items.map(item => {
        if (item.stripe_price_id) {
          return {
            price: item.stripe_price_id,
            quantity: item.quantity,
          };
        }
        return {
          price_data: {
            currency: 'eur',
            product_data: {
              name: item.name,
              images: item.image ? [`${this.API_URL}/assets/${item.image}`] : [],
            },
            unit_amount: Math.round(item.price * 100),
          },
          quantity: item.quantity,
        };
      });

      const response = await fetch(
        `${this.API_URL}/flows/trigger/${this.FLOW_CHECKOUT}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            line_items: lineItems,
            customer_reference_id: userId,
            success_url: successUrl,
            cancel_url: cancelUrl,
            mode: 'payment',
            payment_method_types: ['card', 'multibanco'],
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Falha ao criar sessão de pagamento');
      }

      const data = await response.json();
      return {
        sessionId: data.session_id,
        url: data.url,
      };
    } catch (error) {
      console.error('Error creating Stripe checkout session:', error);
      throw error;
    }
  }

  /**
   * Retrieve Stripe Checkout Session details
   */
  static async getCheckoutSession(sessionId: string): Promise<Record<string, unknown>> {
    try {
      const token = await DirectusService.getToken();

      const response = await fetch(
        `${this.API_URL}/flows/trigger/${this.FLOW_GET_SESSION}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        }
      );

      if (!response.ok) {
        throw new Error('Falha ao obter detalhes da sessão');
      }

      return await response.json();
    } catch (error) {
      console.error('Error retrieving Stripe session:', error);
      throw error;
    }
  }

  /**
   * Redirect to Stripe Checkout
   */
  static redirectToCheckout(checkoutUrl: string): void {
    window.location.href = checkoutUrl;
  }
}
